import React from 'react';
import { DetailsFormProps } from './types';
import { RoleSelection } from './components/RoleSelection';
import { ConflictEffect } from './components/ConflictEffect';
import { OperationsList } from './components/OperationsList';
import { useDetailsForm } from './hooks/useDetailsForm';

/**
 * DetailsForm component for managing policy details configuration
 * 
 * This component provides a complete interface for configuring policy details including:
 * - Role assignments through role definition IDs
 * - Conflict resolution strategy
 * - Policy modification operations
 * 
 * The component maintains a consistent state update pattern, ensuring that all
 * updates preserve existing values for other fields while updating specific sections.
 * 
 * @example
 * <DetailsForm
 *   roleDefinitionIds={['role1', 'role2']}
 *   conflictEffect="audit"
 *   operations={[]}
 *   onUpdate={handleUpdate}
 * />
 */
export const DetailsForm: React.FC<DetailsFormProps> = ({
  roleDefinitionIds,
  conflictEffect,
  operations,
  onUpdate
}) => {
  // Hook-driven form logic for operations list
  const {
    handleAddOperation,
    handleRemoveOperation,
    handleOperationChange,
    updateFieldDropdownPosition,
    fieldSearchTerms,
    setFieldSearchTerms,
    isFieldDropdownOpen,
    setIsFieldDropdownOpen,
    fieldDropdownPositions,
    fieldDropdownRefs,
    fieldInputRefs
  } = useDetailsForm(roleDefinitionIds, conflictEffect, operations, onUpdate);

  /**
   * Handles updates to role assignments (preserves other details)
   */
  const handleUpdateRoles = (newRoleIds: string[]) => {
    onUpdate({ roleDefinitionIds: newRoleIds, ...(conflictEffect && { conflictEffect }), operations });
  };

  /**
   * Handles updates to conflict effect setting (preserves other details)
   */
  const handleUpdateEffect = (newEffect: string | undefined) => {
    onUpdate({ roleDefinitionIds, ...(newEffect && { conflictEffect: newEffect }), operations });
  };

  return (
    // Container without border/shadow styling to remove visible container
    <div className="flex flex-col space-y-6 w-full min-w-0">
      {/* Role selection component */}
      <RoleSelection 
        roleDefinitionIds={roleDefinitionIds}
        onUpdateRoles={handleUpdateRoles}
      />
      
      {/* Conflict effect selection */}
      <ConflictEffect
        conflictEffect={conflictEffect}
        onUpdateEffect={handleUpdateEffect}
      />
      
      {/* Operations list management (uses hook state & handlers) */}
      <OperationsList
        operations={operations}
        onAddOperation={handleAddOperation}
        onRemoveOperation={handleRemoveOperation}
        onOperationChange={handleOperationChange}
        updateFieldDropdownPosition={updateFieldDropdownPosition}
        fieldSearchTerms={fieldSearchTerms}
        setFieldSearchTerms={setFieldSearchTerms}
        isFieldDropdownOpen={isFieldDropdownOpen}
        setIsFieldDropdownOpen={setIsFieldDropdownOpen}
        fieldDropdownPositions={fieldDropdownPositions}
        fieldDropdownRefs={fieldDropdownRefs}
        fieldInputRefs={fieldInputRefs}
      />
    </div>
  );
}; 